/* ==========================================================================
   HP LAB — hp-search-engine.js
   MOTOR ÚNICO de búsqueda, compartido por el diagnóstico y el buscador
   flotante. No hay lógica de búsqueda duplicada en ningún otro archivo.

   Principio central: la colección es el universo de búsqueda (nunca se
   consulta el catálogo completo de Shopify), y la marca es un filtro
   EXCLUYENTE aplicado sobre esa colección — no un factor de ranking.
   Un producto de otra colección o de otra marca queda descartado por
   completo, nunca se muestra como "coincidencia secundaria".

   Fuente de marca/potencia/familia: el TÍTULO del producto (y su
   descripción para familia/texto libre). Es una solución PROVISIONAL —
   documentada así a propósito — hasta que el catálogo tenga tags o
   metafields normalizados. Cambiar la fuente en el futuro solo requiere
   tocar detectProductBrand/detectWattage/familyMatches en este archivo;
   ningún controlador necesita cambiar.
   ========================================================================== */
window.HPLab = window.HPLab || {};

HPLab.SearchEngine = window.HPLab.SearchEngine || (function () {
  "use strict";

  var collectionCache = {};

  function clean(v) {
    return (v || "").toString().trim();
  }

  function escapeRegExp(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  /* --- Colección: SIEMPRE el universo de búsqueda, nunca un fallback tardío. */
  function fetchCollectionProducts(handle, limit) {
    if (!handle) return Promise.resolve([]);
    if (collectionCache[handle]) return collectionCache[handle];
    var url = "/collections/" + encodeURIComponent(handle) + "/products.json?limit=" + (limit || 250);
    var promise = fetch(url)
      .then(function (r) { if (!r.ok) throw new Error("HTTP " + r.status); return r.json(); })
      .then(function (data) { return (data && data.products) || []; })
      .catch(function (err) {
        console.error("HP LAB SearchEngine: error al cargar la colección \"" + handle + "\".", err);
        return [];
      });
    collectionCache[handle] = promise;
    return promise;
  }

  /* --- Detección de marca — fuente provisional: TÍTULO --------------------
     Coincidencia de palabra completa, normalizada (mayúsculas/minúsculas
     indiferentes). Nunca substring / indexOf. Se puede migrar a tags o
     metafields cambiando SOLO esta función. */
  function detectProductBrand(product, brandsList) {
    var title = product && product.title ? product.title : "";
    for (var i = 0; i < (brandsList || []).length; i++) {
      var brand = brandsList[i];
      var re = new RegExp("\\b" + escapeRegExp(brand) + "\\b", "i");
      if (re.test(title)) return brand;
    }
    return null;
  }

  function brandMatches(product, wantedBrand, brandsList) {
    if (!clean(wantedBrand)) return true; // sin marca elegida: no se filtra por marca
    var detected = detectProductBrand(product, brandsList);
    return !!detected && detected.toLowerCase() === clean(wantedBrand).toLowerCase();
  }

  /* --- Potencia — fuente provisional: TÍTULO, normalizada a número -------- */
  function detectWattage(title) {
    var m = (title || "").match(/(\d+)\s*W\b/i);
    return m ? parseInt(m[1], 10) : null;
  }

  function wattageMatches(product, wantedWattage) {
    if (!wantedWattage) return true;
    var detected = detectWattage(product.title);
    return detected !== null && detected === Number(wantedWattage);
  }

  /* --- Familia — palabra completa en título o descripción ------------------ */
  function familyMatches(product, wantedFamily) {
    if (!clean(wantedFamily)) return true;
    var haystack = ((product.title || "") + " " + (product.body_html || ""));
    var re = new RegExp("\\b" + escapeRegExp(clean(wantedFamily)) + "\\b", "i");
    return re.test(haystack);
  }

  /* --- Campos opcionales — refinan, nunca reemplazan colección/marca ------- */
  function optionalFieldMatches(product, value) {
    if (!clean(value)) return true;
    var haystack = ((product.title || "") + " " + (product.body_html || "")).toLowerCase();
    return haystack.indexOf(clean(value).toLowerCase()) !== -1;
  }

  function normalizeProduct(p) {
    var image = (p.images && p.images[0] && p.images[0].src) || (p.image && p.image.src) || "";
    var firstVariant = p.variants && p.variants[0];
    return {
      id: p.id,
      title: p.title,
      url: "/products/" + p.handle,
      image: image,
      price: firstVariant ? firstVariant.price : null,
      available: p.variants ? p.variants.some(function (v) { return v.available; }) : true
    };
  }

  /* --- Búsqueda principal ---------------------------------------------------
     params: {
       component,        "battery" | "charger"
       collectionHandle,
       brandsList,        lista central de marcas válidas (config.brands)
       brand, family, wattage,
       model, productNumber, serial, partNumber, voltage, amperage, connector, freeText
     }
     Orden aplicado: Colección (universo) → Marca (excluyente) →
     Familia/Potencia (excluyente) → Campos opcionales (refinan) → Ranking.
     NUNCA consulta el catálogo completo de Shopify. --------------------------- */
  function search(params) {
    params = params || {};

    return fetchCollectionProducts(params.collectionHandle, 250).then(function (raw) {
      // Filtro 2 y 3: marca + familia/potencia — EXCLUYENTES, no puntajes.
      var filtered = raw.filter(function (p) {
        if (!brandMatches(p, params.brand, params.brandsList)) return false;
        if (params.wattage && !wattageMatches(p, params.wattage)) return false;
        if (params.family && !familyMatches(p, params.family)) return false;
        return true;
      });

      // Filtro 4: campos opcionales — refinan el resultado ya excluyente,
      // nunca reemplazan colección ni marca.
      var optionalValues = [
        params.model, params.productNumber, params.serial, params.partNumber,
        params.voltage, params.amperage, params.connector, params.freeText
      ].filter(function (v) { return clean(v); });

      var refined = filtered;
      var refinedByOptional = false;
      if (optionalValues.length) {
        var withAllOptional = filtered.filter(function (p) {
          return optionalValues.every(function (v) { return optionalFieldMatches(p, v); });
        });
        if (withAllOptional.length) {
          refined = withAllOptional;
          refinedByOptional = true;
        }
      }

      var hasStructuredCriteria = !!(params.family || params.wattage);

      var products = refined.map(function (p) {
        var n = normalizeProduct(p);
        n.tier = (hasStructuredCriteria && refinedByOptional) ? "recommended" : "confirm";
        return n;
      });

      return { products: products, source: "collection-filtered" };
    });
  }

  /* Acción EXPLÍCITA y separada del usuario ("Ver todos los productos de la
     categoría") — nunca se dispara automáticamente como fallback, y nunca
     se presenta como si fueran resultados compatibles. */
  function browseCollection(collectionHandle) {
    return fetchCollectionProducts(collectionHandle, 250).then(function (raw) {
      return {
        products: raw.map(function (p) {
          var n = normalizeProduct(p);
          n.tier = "confirm";
          return n;
        }),
        source: "collection-browse"
      };
    });
  }

  /* --- WhatsApp: única función de normalización de número, para que el
     diagnóstico y el buscador flotante nunca queden desincronizados. ------ */
  function normalizePhone(raw) {
    var digits = clean(raw).replace(/\D/g, "");
    if (!digits) return "";
    if (digits.length === 9 && digits.charAt(0) === "9") return "56" + digits;
    return digits;
  }

  return {
    detectProductBrand: detectProductBrand,
    detectWattage: detectWattage,
    search: search,
    browseCollection: browseCollection,
    normalizePhone: normalizePhone
  };
})();
